// Cookie -> Redis migration (Phase 2) — client-side read helpers.
// The SPA reads device/session data from window.__BRIJ_CTX__, which
// consumer-app-microservice injects during SSR. This is the ONLY source for
// geo/rebate values (the legacy cookies were removed in Phase 2); when the
// context is absent (Redis down, or the page served outside CA-ms such as the
// local Vite dev server), call sites degrade to safe defaults.
//
// Types mirror @brij-it/shared-resources; consolidate to the shared import once
// that package ships them.

interface IpGeo {
  country_code?: string;
  continent_code?: string;
  region_code?: string;
  [key: string]: unknown;
}

interface BrijSession {
  sid?: string;
  did?: string;
  rebate_ids?: Record<string, string>;
  ip_geo?: IpGeo;
  [key: string]: unknown;
}

interface BrijDevice {
  did?: string;
  visit_count?: number;
  [key: string]: unknown;
}

export interface BrijContext {
  device: BrijDevice | null;
  session: BrijSession | null;
}

// ALL [BRIJ_CTX] logging is opt-in: add ?brijDebug=1 to the URL or run
// localStorage.setItem('brijDebug','1'). Without the flag nothing is logged —
// including the failure states (INJECTED-EMPTY / NOT-INJECTED / malformed).
const brijDebugEnabled = (): boolean => {
  if (typeof window === 'undefined') return false;
  try {
    return (
      new URLSearchParams(window.location.search).get('brijDebug') === '1' ||
      window.localStorage.getItem('brijDebug') === '1'
    );
  } catch (e) {
    return false;
  }
};

let bootSummaryLogged = false;
let devStubLogged = false;

export const readBrijContext = (): BrijContext | undefined => {
  if (typeof window === 'undefined') return undefined;
  let ctx = (window as unknown as { __BRIJ_CTX__?: BrijContext }).__BRIJ_CTX__;

  // Dev/QA stub: with debug enabled (?brijDebug=1), an absent context can be
  // supplied via localStorage 'brijDebugCtx' so geo/rebate flows are testable
  // on the local Vite server (where SSR injection never runs). Loudly labeled
  // so it can never be mistaken for real Redis data.
  if (!ctx && brijDebugEnabled()) {
    try {
      const stub = window.localStorage.getItem('brijDebugCtx');
      if (stub) {
        ctx = JSON.parse(stub) as BrijContext;
        if (!devStubLogged) {
          devStubLogged = true;
          console.warn(
            '[BRIJ_CTX] source=DEV-STUB — using localStorage.brijDebugCtx'
          );
        }
      }
    } catch (e) {
      // malformed stub — ignore
    }
  }

  // Boot summary — debug mode only. One line per page load telling you which
  // state the Redis context pipeline is in (REDIS / INJECTED-EMPTY /
  // NOT-INJECTED / malformed).
  if (!bootSummaryLogged && brijDebugEnabled()) {
    bootSummaryLogged = true;
    if (ctx && typeof ctx === 'object' && (ctx.session || ctx.device)) {
      console.log(
        '[BRIJ_CTX] source=REDIS did=%s sid=%s ip_geo=%s visits=%s is_return=%s',
        ctx.device?.did || ctx.session?.did || '(none)',
        ctx.session?.sid || '(none)',
        ctx.session?.ip_geo ? 'present' : 'absent',
        ctx.device?.visit_count ?? '(n/a)',
        ctx.session?.is_return ?? '(n/a)'
      );
    } else if (ctx && typeof ctx === 'object') {
      // Injection ran but both records were null: the read side works — the
      // WRITE side has no data for our ids (redirect service REDIS_URL /
      // NODE_ENV prefix / ids not forwarded), or the 150ms SSR read timed out.
      console.warn(
        '[BRIJ_CTX] source=INJECTED-EMPTY — SSR injection ran but Redis had ' +
          'no device/session records for our ids (check the writer: redirect ' +
          'service REDIS_URL / NODE_ENV key prefix). Using defaults.'
      );
    } else if (ctx) {
      console.error(
        '[BRIJ_CTX] window.__BRIJ_CTX__ is malformed — using defaults:',
        ctx
      );
    } else {
      // Expected on non-/c/ pages (never SSR-injected), local Vite dev,
      // CDN-cached HTML, or an old build.
      console.warn(
        '[BRIJ_CTX] source=NOT-INJECTED — page was not processed by ' +
          'consumer-app-microservice SSR (local dev server, CDN-cached HTML, ' +
          'or old build). Using defaults.'
      );
    }
  }
  return ctx;
};

// Raw ip_geo field from the session record (or undefined if not present).
export const ctxGeo = (field: keyof IpGeo): string | undefined => {
  const value = readBrijContext()?.session?.ip_geo?.[field];
  const resolved = typeof value === 'string' ? value : undefined;
  if (brijDebugEnabled()) {
    console.log(
      '[BRIJ_CTX][read] ip_geo.%s = %s (%s)',
      field,
      resolved ?? '(none)',
      resolved !== undefined ? 'from Redis' : 'not in Redis -> using default'
    );
  }
  return resolved;
};

// Per-slug rebate id from the session record (or undefined). The writer keys
// the map with the redirect service's canonical slug casing while the URL the
// SPA sees can vary, so fall back to case-variants of the slug.
export const ctxRebateId = (slug: string): string | undefined => {
  const ids = readBrijContext()?.session?.rebate_ids;
  const resolved =
    ids?.[slug] ?? ids?.[slug.toUpperCase()] ?? ids?.[slug.toLowerCase()];
  if (brijDebugEnabled()) {
    console.log(
      '[BRIJ_CTX][read] rebate_ids[%s] = %s (%s)',
      slug,
      resolved ?? '(none)',
      resolved !== undefined ? 'from Redis' : 'not in Redis -> using default'
    );
  }
  return resolved;
};
